import Vector from "./Vector.js";

const IDENTITY = [
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1
];

export default class Matrix {

    /**
     * Creates a new matrix.  
     * `new Matrix()` Creates a new identity matrix.  
     * `new Matrix(vals)` Creates a new matrix from 16 values row-major.
     */
    constructor() {
        let len = arguments.length;
        if (len == 0 || len == 16) {
            if (len == 0) this.m = new Float32Array(IDENTITY);
            else this.m = new Float32Array(arguments);
        } else {
            throw new Error("Unable to create matrix. Invalid parameter count ( must be either 0 or 16 )");
        }
    }

    /**
     * Computes the inverse of this matrix and returns it.
     */
    inverse() {
        return Matrix.inverse(this, new Matrix());
    }

    /**
     * Computes the transpose of this matrix and returns it.
     */
    transpose() {
        return Matrix.transpose(this, new Matrix());
    }

    /**
     * Computes the multiplication of this matrix against the given matrix.
     * @param {Matrix} matrix 
     */
    multiply(matrix) {
        if (!matrix instanceof Matrix) throw new Error("Unable to multiply matrices. Argument not a matrix.");
        return Matrix.multiply(this, matrix, new Matrix());
    }

    /**
     * Computes the transformation of the given vector due to this matrix ( with perspective divide ).
     * @param {Vector} v
     */
    transformPoint(v) {
        // transform w/ perspective divide
        var m = this.m;
        return new Vector(
            m[0] * v.x + m[1] * v.y + m[2] * v.z + m[3],
            m[4] * v.x + m[5] * v.y + m[6] * v.z + m[7],
            m[8] * v.x + m[9] * v.y + m[10] * v.z + m[11]
        ).divide(m[12] * v.x + m[13] * v.y + m[14] * v.z + m[15]);
    }

    /**
     * Computes the transformation of the given vector due to this matrix.
     * @param {Vector} v
     */
    transformVector(v) {
        // transform w/o perspective divide
        var m = this.m;
        return new Vector(
            m[0] * v.x + m[1] * v.y + m[2] * v.z,
            m[4] * v.x + m[5] * v.y + m[6] * v.z,
            m[8] * v.x + m[9] * v.y + m[10] * v.z
        );
    }

    /**
     * Returns an array with `[m[0]...m[15]]`.
     */
    toArray() {
        return [...m];
    }

    /**
     * Computes the inverse of the given matrix.
     * @param {Matrix} matrix Some input matrix
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static inverse(matrix, storage) {
        storage = storage || new Matrix();
        var m = matrix.m, r = storage.m;

        r[0] = m[5] * m[10] * m[15] - m[5] * m[14] * m[11] - m[6] * m[9] * m[15] + m[6] * m[13] * m[11] + m[7] * m[9] * m[14] - m[7] * m[13] * m[10];
        r[1] = -m[1] * m[10] * m[15] + m[1] * m[14] * m[11] + m[2] * m[9] * m[15] - m[2] * m[13] * m[11] - m[3] * m[9] * m[14] + m[3] * m[13] * m[10];
        r[2] = m[1] * m[6] * m[15] - m[1] * m[14] * m[7] - m[2] * m[5] * m[15] + m[2] * m[13] * m[7] + m[3] * m[5] * m[14] - m[3] * m[13] * m[6];
        r[3] = -m[1] * m[6] * m[11] + m[1] * m[10] * m[7] + m[2] * m[5] * m[11] - m[2] * m[9] * m[7] - m[3] * m[5] * m[10] + m[3] * m[9] * m[6];

        r[4] = -m[4] * m[10] * m[15] + m[4] * m[14] * m[11] + m[6] * m[8] * m[15] - m[6] * m[12] * m[11] - m[7] * m[8] * m[14] + m[7] * m[12] * m[10];
        r[5] = m[0] * m[10] * m[15] - m[0] * m[14] * m[11] - m[2] * m[8] * m[15] + m[2] * m[12] * m[11] + m[3] * m[8] * m[14] - m[3] * m[12] * m[10];
        r[6] = -m[0] * m[6] * m[15] + m[0] * m[14] * m[7] + m[2] * m[4] * m[15] - m[2] * m[12] * m[7] - m[3] * m[4] * m[14] + m[3] * m[12] * m[6];
        r[7] = m[0] * m[6] * m[11] - m[0] * m[10] * m[7] - m[2] * m[4] * m[11] + m[2] * m[8] * m[7] + m[3] * m[4] * m[10] - m[3] * m[8] * m[6];

        r[8] = m[4] * m[9] * m[15] - m[4] * m[13] * m[11] - m[5] * m[8] * m[15] + m[5] * m[12] * m[11] + m[7] * m[8] * m[13] - m[7] * m[12] * m[9];
        r[9] = -m[0] * m[9] * m[15] + m[0] * m[13] * m[11] + m[1] * m[8] * m[15] - m[1] * m[12] * m[11] - m[3] * m[8] * m[13] + m[3] * m[12] * m[9];
        r[10] = m[0] * m[5] * m[15] - m[0] * m[13] * m[7] - m[1] * m[4] * m[15] + m[1] * m[12] * m[7] + m[3] * m[4] * m[13] - m[3] * m[12] * m[5];
        r[11] = -m[0] * m[5] * m[11] + m[0] * m[9] * m[7] + m[1] * m[4] * m[11] - m[1] * m[8] * m[7] - m[3] * m[4] * m[9] + m[3] * m[8] * m[5];

        r[12] = -m[4] * m[9] * m[14] + m[4] * m[13] * m[10] + m[5] * m[8] * m[14] - m[5] * m[12] * m[10] - m[6] * m[8] * m[13] + m[6] * m[12] * m[9];
        r[13] = m[0] * m[9] * m[14] - m[0] * m[13] * m[10] - m[1] * m[8] * m[14] + m[1] * m[12] * m[10] + m[2] * m[8] * m[13] - m[2] * m[12] * m[9];
        r[14] = -m[0] * m[5] * m[14] + m[0] * m[13] * m[6] + m[1] * m[4] * m[14] - m[1] * m[12] * m[6] - m[2] * m[4] * m[13] + m[2] * m[12] * m[5];
        r[15] = m[0] * m[5] * m[10] - m[0] * m[9] * m[6] - m[1] * m[4] * m[10] + m[1] * m[8] * m[6] + m[2] * m[4] * m[9] - m[2] * m[8] * m[5];

        var det = m[0] * r[0] + m[1] * r[4] + m[2] * r[8] + m[3] * r[12];
        for (var i = 0; i < 16; i++) r[i] /= det;
        return storage;
    };

    /**
     * Computes the transpose of the given matrix.
     * @param {Matrix} matrix Some input matrix
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static transpose(matrix, storage) {
        storage = storage || new Matrix();
        var m = matrix.m, r = storage.m;
        r[0] = m[0]; r[1] = m[4]; r[2] = m[8]; r[3] = m[12];
        r[4] = m[1]; r[5] = m[5]; r[6] = m[9]; r[7] = m[13];
        r[8] = m[2]; r[9] = m[6]; r[10] = m[10]; r[11] = m[14];
        r[12] = m[3]; r[13] = m[7]; r[14] = m[11]; r[15] = m[15];
        return storage;
    };

    /**
     * Computes the transpose of the given matrix.
     * @param {Matrix} left The left hand matrix
     * @param {Matrix} right The right hand matrix
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static multiply(left, right, storage) {
        storage = storage || new Matrix();
        var a = left.m, b = right.m, r = storage.m;

        r[0] = a[0] * b[0] + a[1] * b[4] + a[2] * b[8] + a[3] * b[12];
        r[1] = a[0] * b[1] + a[1] * b[5] + a[2] * b[9] + a[3] * b[13];
        r[2] = a[0] * b[2] + a[1] * b[6] + a[2] * b[10] + a[3] * b[14];
        r[3] = a[0] * b[3] + a[1] * b[7] + a[2] * b[11] + a[3] * b[15];

        r[4] = a[4] * b[0] + a[5] * b[4] + a[6] * b[8] + a[7] * b[12];
        r[5] = a[4] * b[1] + a[5] * b[5] + a[6] * b[9] + a[7] * b[13];
        r[6] = a[4] * b[2] + a[5] * b[6] + a[6] * b[10] + a[7] * b[14];
        r[7] = a[4] * b[3] + a[5] * b[7] + a[6] * b[11] + a[7] * b[15];

        r[8] = a[8] * b[0] + a[9] * b[4] + a[10] * b[8] + a[11] * b[12];
        r[9] = a[8] * b[1] + a[9] * b[5] + a[10] * b[9] + a[11] * b[13];
        r[10] = a[8] * b[2] + a[9] * b[6] + a[10] * b[10] + a[11] * b[14];
        r[11] = a[8] * b[3] + a[9] * b[7] + a[10] * b[11] + a[11] * b[15];

        r[12] = a[12] * b[0] + a[13] * b[4] + a[14] * b[8] + a[15] * b[12];
        r[13] = a[12] * b[1] + a[13] * b[5] + a[14] * b[9] + a[15] * b[13];
        r[14] = a[12] * b[2] + a[13] * b[6] + a[14] * b[10] + a[15] * b[14];
        r[15] = a[12] * b[3] + a[13] * b[7] + a[14] * b[11] + a[15] * b[15];

        return storage;
    };

    /**
     * Computes a perspective matrix.
     * @param {Number} fov Field of view ( in degree )
     * @param {Number} aspect Aspect ratio of the render target / screen.
     * @param {Number} near The distance of the near clipping plane.
     * @param {Number} far The distance of the far clipping plane.
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static perspective(fov, aspect, near, far, storage) {
        var y = Math.tan(fov * (Math.PI / 180) / 2) * near;
        var x = y * aspect;
        return Matrix.frustum(-x, x, -y, y, near, far, storage);

        // storage = storage || new Matrix();
        // var m = storage.m;

        // let scale = 1 / Math.tan(fov * (Math.PI / 180) * 0.5);
        // let zRange = far - near;

        // m[0] = scale / aspect;
        // m[5] = scale; // 11
        // m[9] = -far / zRange;
        // m[14] = -far * near / zRange;
        // m[11] = -1;
        // m[17] = 0;

        // return storage;
    };

    /**
     * Computes a frustum transform matrix.
     * @param {Number} l Left plane
     * @param {Number} r Right plane
     * @param {Number} b Bottom plane
     * @param {Number} t Top plane
     * @param {Number} n Near plane
     * @param {Number} f Far plane
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static frustum(l, r, b, t, n, f, storage) {
        storage = storage || new Matrix();
        var m = storage.m;

        m[0] = 2 * n / (r - l);
        m[1] = 0;
        m[2] = (r + l) / (r - l);
        m[3] = 0;

        m[4] = 0;
        m[5] = 2 * n / (t - b);
        m[6] = (t + b) / (t - b);
        m[7] = 0;

        m[8] = 0;
        m[9] = 0;
        m[10] = -(f + n) / (f - n);
        m[11] = -2 * f * n / (f - n);

        m[12] = 0;
        m[13] = 0;
        m[14] = -1;
        m[15] = 0;

        return storage;
    };

    /**
     * Computes an orthographic projection matrix.
     * @param {Number} l Left plane
     * @param {Number} r Right plane
     * @param {Number} b Bottom plane
     * @param {Number} t Top plane
     * @param {Number} n Near plane
     * @param {Number} f Far plane
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static ortho(l, r, b, t, n, f, result) {
        result = result || new Matrix();
        var m = result.m;

        m[0] = 2 / (r - l);
        m[1] = 0;
        m[2] = 0;
        m[3] = -(r + l) / (r - l);

        m[4] = 0;
        m[5] = 2 / (t - b);
        m[6] = 0;
        m[7] = -(t + b) / (t - b);

        m[8] = 0;
        m[9] = 0;
        m[10] = -2 / (f - n);
        m[11] = -(f + n) / (f - n);

        m[12] = 0;
        m[13] = 0;
        m[14] = 0;
        m[15] = 1;

        return result;
    };

    /**
     * Computes a scaling matrix.
     * @param {Number} x Scaling factor along the x-axis.
     * @param {Number} y Scaling factor along the y-axis.
     * @param {Number} z Scaling factor along the z-axis.
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static scale(x, y, z, storage) {
        storage = storage || new Matrix();
        var m = storage.m;

        m[0] = x;
        m[1] = 0;
        m[2] = 0;
        m[3] = 0;

        m[4] = 0;
        m[5] = y;
        m[6] = 0;
        m[7] = 0;

        m[8] = 0;
        m[9] = 0;
        m[10] = z;
        m[11] = 0;

        m[12] = 0;
        m[13] = 0;
        m[14] = 0;
        m[15] = 1;

        return storage;
    };

    /**
     * Computes a translation matrix.
     * @param {Number} x Translation factor along the x-axis.
     * @param {Number} y Translation factor along the y-axis.
     * @param {Number} z Translation factor along the z-axis.
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static translate(x, y, z, storage) {
        storage = storage || new Matrix();
        var m = storage.m;

        m[0] = 1;
        m[1] = 0;
        m[2] = 0;
        m[3] = x;

        m[4] = 0;
        m[5] = 1;
        m[6] = 0;
        m[7] = y;

        m[8] = 0;
        m[9] = 0;
        m[10] = 1;
        m[11] = z;

        m[12] = 0;
        m[13] = 0;
        m[14] = 0;
        m[15] = 1;

        return storage;
    };

    /**
     * Computes an angle-axis rotation matrix.
     * @param {Number} a Angle of rotation
     * @param {Number} x The x component of the rotation axis.
     * @param {Number} y The y component of the rotation axis.
     * @param {Number} z The z component of the rotation axis.
     * @param {Matrix} storage The destination matrix for computed values.
     */
    static rotate(a, x, y, z, storage) {
        if (!a || (!x && !y && !z)) {
            return Matrix.identity(storage);
        }

        storage = storage || new Matrix();
        var m = storage.m;

        var d = Math.sqrt(x * x + y * y + z * z);
        a *= Math.PI / 180; x /= d; y /= d; z /= d;
        var c = Math.cos(a), s = Math.sin(a), t = 1 - c;

        m[0] = x * x * t + c;
        m[1] = x * y * t - z * s;
        m[2] = x * z * t + y * s;
        m[3] = 0;

        m[4] = y * x * t + z * s;
        m[5] = y * y * t + c;
        m[6] = y * z * t - x * s;
        m[7] = 0;

        m[8] = z * x * t - y * s;
        m[9] = z * y * t + x * s;
        m[10] = z * z * t + c;
        m[11] = 0;

        m[12] = 0;
        m[13] = 0;
        m[14] = 0;
        m[15] = 1;

        return storage;
    };

    static lookAt(eye, center, up, storage) {
        storage = storage || new Matrix();
        var m = storage.m;

        var f = eye.sub(center).unit();
        var s = up.cross(f).unit();
        var t = f.cross(s).unit();

        m[0] = s.x;
        m[1] = s.y;
        m[2] = s.z;

        m[4] = t.x;
        m[5] = t.y;
        m[6] = t.z;

        m[8] = f.x;
        m[9] = f.y;
        m[10] = f.z;

        m[3] = -s.dot(eye);
        m[7] = -t.dot(eye);
        m[11] = -f.dot(eye);

        return storage;
    };
}